import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchFilenameSummary,
  FetchFilenameSummaryParams,
  FilenameSummaryApiResponse,
  FilenameSummaryItem,
} from "../../../../api/bigQuery/filenameSummary";
import { SftpPushPendingRecordsTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

export interface CheckboxData {
  id: string;
  label: string;
  isChecked: boolean;
  disabled?: boolean;
}

export interface SftpPushPendingRecordsTableDataModel {
  [SftpPushPendingRecordsTableColumnKeys.SELECT]: CheckboxData;
  [SftpPushPendingRecordsTableColumnKeys.FILENAME]: string;
  [SftpPushPendingRecordsTableColumnKeys.ROW_COUNT]: number;
  [SftpPushPendingRecordsTableColumnKeys.RESUBMIT_COUNT]: number;
  [SftpPushPendingRecordsTableColumnKeys.PENDING_COUNT]: number;
  [SftpPushPendingRecordsTableColumnKeys.DUMPED_AT]: string;
  [SftpPushPendingRecordsTableColumnKeys.RECEIVE_TIME]: string;
}

interface UseSftpPushPendingRecordsState {
  data: FilenameSummaryApiResponse | null;
  loading: boolean;
  showInlineLoader: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch SFTP push pending records on mount
 * Uses the same API as PushToSftp modal (fetchFilenameSummary), with the
 * "sftp_push_pending" view so the backend returns only filenames holding rows
 * with sftp_status='resubmit' and a terminal response_status
 * Supports pagination with infinite scroll pattern
 */
export const useSftpPushPendingRecords = (
  params: FetchFilenameSummaryParams = { page: 1, page_size: 120 },
) => {
  const [tableData, setTableData] = useState<
    SftpPushPendingRecordsTableDataModel[]
  >([]);
  const [endIndex, setEndIndex] = useState<number>(0);
  const [state, setState] = useState<UseSftpPushPendingRecordsState>({
    data: null,
    loading: true,
    showInlineLoader: false,
    error: null,
  });

  const pageSize = params.page_size ?? 120;

  // Transform API response items to table data format
  // The "sftp_push_pending" view already restricts the response to files with
  // pending resubmits, so no client-side filtering is needed
  const transformResponseToTableData = useCallback(
    (
      items: FilenameSummaryItem[],
      startIndex: number,
    ): SftpPushPendingRecordsTableDataModel[] => {
      return items.map((item, index) => ({
        [SftpPushPendingRecordsTableColumnKeys.SELECT]: {
          id: `row-${startIndex + index}`,
          label: "",
          isChecked: false,
          disabled: false,
        },
        [SftpPushPendingRecordsTableColumnKeys.FILENAME]: item.filename,
        [SftpPushPendingRecordsTableColumnKeys.ROW_COUNT]: item.row_count,
        [SftpPushPendingRecordsTableColumnKeys.RESUBMIT_COUNT]:
          item.resubmit_count,
        [SftpPushPendingRecordsTableColumnKeys.PENDING_COUNT]:
          item.pending_count,
        [SftpPushPendingRecordsTableColumnKeys.DUMPED_AT]: moment(
          item.dumped_at,
        ).format("MM/DD/YYYY"),
        [SftpPushPendingRecordsTableColumnKeys.RECEIVE_TIME]: moment(
          item.dumped_at,
        ).format("HH:mm"),
      }));
    },
    [],
  );

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const orgId = getOrgIdForFetchExternalWorklist();
        const response = await fetchFilenameSummary({
          page: 1,
          page_size: pageSize,
          org_id: orgId,
          view: "sftp_push_pending",
          order_by: [
            { column: "dumped_at", direction: "DESC" },
            { column: "filename", direction: "ASC" },
          ],
        });
        if (response.success && response.items) {
          const transformedData = transformResponseToTableData(
            response.items,
            0,
          );
          setTableData(transformedData);
          setState({
            data: response,
            loading: false,
            showInlineLoader: false,
            error: null,
          });
        }
      } catch (error) {
        console.error("Error fetching SFTP push pending records:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : "Unknown error",
        }));
      }
    };

    fetchInitialData();
  }, [pageSize, transformResponseToTableData]);

  // Calculate if we should fetch more data
  const shouldFetchMoreData = useMemo(() => {
    const totalRowsLoaded = tableData.length;
    const currentPage = state.data?.page ?? 1;
    const totalPages = state.data?.total_pages ?? 1;

    return (
      endIndex === totalRowsLoaded - 1 &&
      currentPage < totalPages &&
      !state.loading &&
      !state.showInlineLoader
    );
  }, [
    endIndex,
    tableData.length,
    state.data?.page,
    state.data?.total_pages,
    state.loading,
    state.showInlineLoader,
  ]);

  // Refs to store latest values
  const setStateRef = useRef(setState);
  const setTableDataRef = useRef(setTableData);

  useEffect(() => {
    setStateRef.current = setState;
    setTableDataRef.current = setTableData;
  }, [setState, setTableData]);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    const nextPage = (state.data?.page ?? 0) + 1;

    setStateRef.current((prev) => ({
      ...prev,
      showInlineLoader: true,
    }));

    try {
      // Must match the initial fetch: for an external org that helper prefers
      // INTERNAL_ORGANIZATION_FACILITY_ID, so reading HEALTHCARE_FACILITY_ID
      // directly here paged through a different organisation's files.
      const orgId = getOrgIdForFetchExternalWorklist();
      const response = await fetchFilenameSummary({
        page: nextPage,
        page_size: pageSize,
        org_id: orgId,
        view: "sftp_push_pending",
        order_by: [
          { column: "dumped_at", direction: "DESC" },
          { column: "filename", direction: "ASC" },
        ],
      });

      if (response.success && response.items) {
        const startIndex = tableData.length;
        const transformedData = transformResponseToTableData(
          response.items,
          startIndex,
        );

        setTableDataRef.current((prevData) => [
          ...prevData,
          ...transformedData,
        ]);
        setStateRef.current({
          data: response,
          loading: false,
          showInlineLoader: false,
          error: null,
        });
      }
    } catch (error) {
      console.error("Error fetching next page:", error);
      setStateRef.current((prev) => ({
        ...prev,
        showInlineLoader: false,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }
  }, [
    state.data?.page,
    pageSize,
    transformResponseToTableData,
    tableData.length,
  ]);

  // Trigger fetch when needed
  useEffect(() => {
    if (shouldFetchMoreData) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  // Calculate if there are more pages to load (for showing loading indicator)
  const hasMorePages = (state.data?.page ?? 1) < (state.data?.total_pages ?? 1);

  // Only return count after initial data has loaded
  // This prevents "Gathering metadata..." from showing during initial load
  const hasInitialDataLoaded = state.data !== null;

  return {
    tableData,
    setTableData,
    isLoading: state.loading,
    showInlineLoader: state.showInlineLoader,
    // The "sftp_push_pending" view filters server-side, so total_count is the
    // true count of matching files rather than just the rows loaded so far
    // Only return actual count after initial load to avoid "Gathering metadata..."
    totalCount: hasInitialDataLoaded ? state.data?.total_count : undefined,
    setEndIndex,
    error: state.error,
    hasMorePages,
  };
};
